import { useState, useEffect } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Mail, MapPin } from "lucide-react";
import { Reveal } from "./Reveal";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BRAND, PROJECT_TYPES, CTA_OPTIONS } from "@/data/content";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ENGAGEMENTS = CTA_OPTIONS.map((c) => c.title);

const FieldLine = ({ label, ...props }) => (
  <label className="block">
    <span className="overline text-white/40 block mb-3">{label}</span>
    <input
      {...props}
      className="w-full bg-transparent border-b border-white/20 py-3 text-white text-lg font-light placeholder:text-white/25 focus:border-[#d4af37] outline-none transition-colors duration-300"
    />
  </label>
);

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    project_type: "",
    inquiry_type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const handler = (e) => setForm((f) => ({ ...f, inquiry_type: e.detail }));
    window.addEventListener("edc:set-inquiry", handler);
    return () => window.removeEventListener("edc:set-inquiry", handler);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Received. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", company: "", project_type: "", inquiry_type: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact" className="bg-[#080808] border-t border-white/10">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-28 md:py-40 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <Reveal className="lg:col-span-5">
          <p className="overline text-[#d4af37] mb-6">Start a Conversation</p>
          <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1.02] tracking-tight text-white">
            Let’s protect your next project.
          </h2>
          <p className="mt-8 text-white/60 font-light leading-relaxed max-w-md">
            Tell us about the development. We’ll respond with a clear view of how owner-side
            advisory can de-risk your capital and sharpen your returns.
          </p>

          <div className="mt-12 space-y-5">
            <a data-testid="contact-phone" href={BRAND.phoneHref} className="flex items-center gap-4 text-white/80 hover:text-[#d4af37] transition-colors">
              <Phone size={18} className="text-[#d4af37]" />
              <span className="text-lg font-light">{BRAND.phone}</span>
            </a>
            <a data-testid="contact-email" href={BRAND.emailHref} className="flex items-center gap-4 text-white/80 hover:text-[#d4af37] transition-colors">
              <Mail size={18} className="text-[#d4af37]" />
              <span className="text-lg font-light break-all">{BRAND.email}</span>
            </a>
            <p className="text-white/40 text-sm pt-2">{BRAND.serviceArea}</p>
            <div className="pt-4 flex items-start gap-4 text-white/60" data-testid="contact-address">
              <MapPin size={18} className="text-[#d4af37] mt-1 shrink-0" />
              <span className="font-light leading-relaxed">
                {BRAND.address[0]}
                <br />
                {BRAND.address[1]}
              </span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-6 lg:col-start-7">
          <form data-testid="contact-form" onSubmit={submit} className="space-y-10">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <FieldLine label="Full Name *" data-testid="contact-name" value={form.name} onChange={set("name")} placeholder="Jane Owner" />
              <FieldLine label="Email *" data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} placeholder="jane@company.com" />
              <FieldLine label="Phone" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" />
              <FieldLine label="Company" data-testid="contact-company" value={form.company} onChange={set("company")} placeholder="Optional" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <label className="block">
                <span className="overline text-white/40 block mb-3">Type of Engagement</span>
                <Select value={form.inquiry_type} onValueChange={(v) => setForm((f) => ({ ...f, inquiry_type: v }))}>
                  <SelectTrigger
                    data-testid="contact-inquiry-type"
                    className="w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 h-auto text-white text-lg font-light focus:border-[#d4af37] focus:ring-0"
                  >
                    <SelectValue placeholder="Select an engagement" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0e0e0e] border-white/10 text-white">
                    {ENGAGEMENTS.map((p) => (
                      <SelectItem key={p} value={p} className="focus:bg-[#d4af37]/15 focus:text-white">
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>

              <label className="block">
                <span className="overline text-white/40 block mb-3">Project Type</span>
                <Select value={form.project_type} onValueChange={(v) => setForm((f) => ({ ...f, project_type: v }))}>
                  <SelectTrigger
                    data-testid="contact-project-type"
                    className="w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 h-auto text-white text-lg font-light focus:border-[#d4af37] focus:ring-0"
                  >
                    <SelectValue placeholder="Select a project type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0e0e0e] border-white/10 text-white">
                    {PROJECT_TYPES.map((p) => (
                      <SelectItem key={p} value={p} className="focus:bg-[#d4af37]/15 focus:text-white">
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
            </div>

            <label className="block">
              <span className="overline text-white/40 block mb-3">Your Message *</span>
              <textarea
                data-testid="contact-message"
                value={form.message}
                onChange={set("message")}
                rows={4}
                placeholder="A few lines about the project, timeline and where you need support."
                className="w-full bg-transparent border-b border-white/20 py-3 text-white text-lg font-light placeholder:text-white/25 focus:border-[#d4af37] outline-none transition-colors duration-300 resize-none"
              />
            </label>

            <motion.button
              data-testid="contact-submit"
              type="submit"
              disabled={submitting}
              whileHover={{ x: 6 }}
              className="btn-gold rounded-full px-9 py-4 text-xs font-semibold tracking-wider uppercase inline-flex items-center gap-3 disabled:opacity-60"
            >
              {submitting ? "Sending..." : "Send Inquiry"}
              <ArrowRight size={16} />
            </motion.button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
